import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { AVATAR_COUNT, getAvatarPath, AVATAR_LABELS } from '@/lib/avatar-utils';
import { cn } from '@/lib/utils';

const TOTAL_STEPS = 3;

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? '-100%' : '100%',
    opacity: 0,
  }),
};

const avatarContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.04 },
  },
};

const avatarItemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0 },
};

const UnifiedOnboarding = () => {
  const { updateDisplayName, updateAvatar, skipAvatar, completeOnboarding } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [completing, setCompleting] = useState(false);

  // Step 0 — Display Name
  const [name, setName] = useState('');
  const [savingName, setSavingName] = useState(false);

  // Step 1 — Avatar
  const [selectedAvatarId, setSelectedAvatarId] = useState<number | null>(null);
  const [savingAvatar, setSavingAvatar] = useState(false);

  const goTo = (nextStep: number) => {
    setDirection(nextStep > step ? 1 : -1);
    setStep(nextStep);
  };

  const handleSkip = async () => {
    if (completing) return;
    setCompleting(true);
    await completeOnboarding();
    localStorage.setItem('br_ios_install_trigger', 'true');
    const pending = localStorage.getItem('pendingInvitePath');
    navigate(pending || '/');
  };

  const handleFinish = async () => {
    if (completing) return;
    setCompleting(true);
    await completeOnboarding();
    localStorage.setItem('br_ios_install_trigger', 'true');
    const pending = localStorage.getItem('pendingInvitePath');
    navigate(pending || '/');
  };

  // Touch swipe tracking
  const [touchStartX, setTouchStartX] = useState<number | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX === null) return;
    const delta = touchStartX - e.changedTouches[0].clientX;
    if (Math.abs(delta) > 50) {
      if (delta > 0 && step < TOTAL_STEPS - 1) goTo(step + 1);
      else if (delta < 0 && step > 0) goTo(step - 1);
    }
    setTouchStartX(null);
  };

  // Step 0: Display Name
  const handleNameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSavingName(true);
    await updateDisplayName(name.trim());
    setSavingName(false);
    goTo(1);
  };

  // Step 1: Avatar — confirm
  const handleAvatarConfirm = async () => {
    if (selectedAvatarId === null) return;
    setSavingAvatar(true);
    await updateAvatar(selectedAvatarId);
    setSavingAvatar(false);
    goTo(2);
  };

  // Step 1: Avatar — skip
  const handleAvatarSkip = async () => {
    await skipAvatar();
    goTo(2);
  };

  const renderStep = (currentStep: number) => {
    switch (currentStep) {
      case 0:
        return (
          <div className="flex flex-col items-center gap-6 text-center w-full max-w-sm">
            <img src="/images/logo.png" alt="Bracket Run" className="h-14 w-auto" />
            <div>
              <h2 className="text-2xl font-bold mb-2">ברוכים הבאים!</h2>
              <p className="text-muted-foreground">איך לקרוא לך?</p>
            </div>
            <form onSubmit={handleNameSubmit} className="w-full space-y-3">
              <Input
                placeholder="שם תצוגה"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-secondary border-border text-right"
                autoFocus
              />
              <Button
                type="submit"
                variant="accent"
                size="lg"
                className="w-full"
                disabled={savingName || !name.trim()}
              >
                {savingName ? 'שומר...' : 'הבא'}
              </Button>
            </form>
          </div>
        );

      case 1:
        return (
          <div className="flex flex-col items-center gap-4 w-full max-w-lg" style={{ maxHeight: 'calc(100vh - 140px)' }}>
            <div className="text-center shrink-0">
              <h2 className="text-2xl font-bold mb-1">בחר את הדמות שלך</h2>
              <p className="text-sm text-muted-foreground">האווטאר שיופיע בלוח התוצאות</p>
            </div>
            <motion.div
              className="grid grid-cols-3 md:grid-cols-5 gap-3 w-full overflow-y-auto flex-1 min-h-0 pb-2"
              variants={avatarContainerVariants}
              initial="hidden"
              animate="visible"
            >
              {Array.from({ length: AVATAR_COUNT }, (_, i) => i + 1).map((id) => (
                <motion.button
                  key={id}
                  variants={avatarItemVariants}
                  onClick={() => setSelectedAvatarId(id)}
                  className={cn(
                    'relative rounded-2xl p-1 transition-all duration-200 focus:outline-none',
                    selectedAvatarId === id
                      ? 'border-2 border-accent ring-2 ring-accent/30 scale-105'
                      : 'border-2 border-transparent hover:border-border hover:scale-102'
                  )}
                  title={AVATAR_LABELS[id]}
                  aria-label={AVATAR_LABELS[id]}
                  aria-pressed={selectedAvatarId === id}
                >
                  <img
                    src={getAvatarPath(id)}
                    alt={AVATAR_LABELS[id]}
                    className="w-full aspect-square rounded-xl object-cover"
                    loading="lazy"
                  />
                  {selectedAvatarId === id && (
                    <div className="absolute inset-0 rounded-2xl bg-accent/10 pointer-events-none" />
                  )}
                </motion.button>
              ))}
            </motion.div>
            <div className="flex gap-3 w-full shrink-0">
              <Button
                variant="ghost"
                className="flex-1 text-muted-foreground"
                onClick={handleAvatarSkip}
                disabled={savingAvatar}
              >
                דלג
              </Button>
              <Button
                variant="accent"
                className="flex-1"
                disabled={selectedAvatarId === null || savingAvatar}
                onClick={handleAvatarConfirm}
              >
                {savingAvatar ? 'שומר...' : 'בחירה'}
              </Button>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="flex flex-col items-center justify-center gap-6 text-center w-full max-w-sm">
            <img src="/images/logo.png" alt="Bracket Run" className="h-16 w-auto" />
            <h2 className="text-2xl font-bold">ברוכים הבאים!</h2>
            <p className="text-muted-foreground text-lg max-w-xs">
              נחשו תוצאות פלייאוף ותתחרו עם החברים
            </p>
            <div className="w-full max-w-xs">
              <p className="section-label mb-3">דוגמה</p>
              <p className="text-sm text-muted-foreground mb-4">ניחשת: Thunder 4-2 (סביר - פייבוריט קל)</p>
              <div className="flex items-center gap-2 justify-center">
                <div className="bg-card border border-border rounded-xl p-3 text-center">
                  <p className="text-xs text-muted-foreground">מנצחת</p>
                  <p className="text-lg font-bold text-green-400">+2</p>
                </div>
                <span className="text-muted-foreground text-lg">+</span>
                <div className="bg-card border border-border rounded-xl p-3 text-center">
                  <p className="text-xs text-muted-foreground">תוצאה מדויקת</p>
                  <p className="text-lg font-bold text-accent">+3</p>
                </div>
                <span className="text-muted-foreground text-lg">=</span>
                <div className="bg-card border border-accent rounded-xl p-3 text-center">
                  <p className="text-xs text-muted-foreground">סה"כ</p>
                  <p className="text-xl font-bold text-accent">5</p>
                </div>
              </div>
            </div>
            <Button
              variant="accent"
              size="lg"
              className="w-full max-w-xs"
              onClick={handleFinish}
              disabled={completing}
            >
              {completing ? 'נכנסים...' : 'יאללה, מתחילים!'}
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-background flex flex-col"
      dir="rtl"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Skip button — steps 1-2 only */}
      <div className="flex justify-start p-4 min-h-[52px]">
        {step > 0 && (
          <button
            onClick={handleSkip}
            disabled={completing}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors px-2 py-1"
          >
            דלג
          </button>
        )}
      </div>

      {/* Main content area */}
      <div className="flex-1 relative overflow-hidden flex items-center justify-center px-6">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={step}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="absolute inset-0 flex items-center justify-center px-6 py-4"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: 0.05 }}
              className="w-full max-w-lg flex justify-center"
            >
              {renderStep(step)}
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Progress dots */}
      <div className="flex justify-center gap-2 pb-12">
        {Array.from({ length: TOTAL_STEPS }, (_, i) => (
          <div
            key={i}
            className={cn(
              'h-2.5 rounded-full transition-all duration-200',
              i === step ? 'bg-accent w-6' : 'bg-muted w-2.5'
            )}
            aria-label={`שלב ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default UnifiedOnboarding;
